import type { MangaChapter, MangaItem } from "@/mocks/comics";
import { readerPages } from "@/mocks/readerPages";

interface ReaderProps {
  comic: MangaItem;
  chapter: MangaChapter;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

export default function Reader({ comic, chapter, hasPrev, hasNext, onPrev, onNext }: ReaderProps) {
  return (
    <div id="reader" className="scroll-mt-24">
      <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200/70 bg-background-100/60 p-4 text-center md:p-6">
        <div className="flex w-full items-center justify-between gap-3">
          <button
            type="button"
            onClick={onPrev}
            disabled={!hasPrev}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-foreground-950/15 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-foreground-950/15 disabled:hover:text-foreground-800 md:px-4 md:text-sm cursor-pointer"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            <span className="hidden sm:inline">上一章</span>
          </button>
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950 md:text-lg">
              《{comic.title}》 {chapter.title}
            </h2>
            <p className="mt-1 text-xs text-foreground-600">共 {readerPages.length} 页 · 免费阅读 · 支持双指缩放</p>
          </div>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-foreground-950/15 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-foreground-950/15 disabled:hover:text-foreground-800 md:px-4 md:text-sm cursor-pointer"
          >
            <span className="hidden sm:inline">下一章</span>
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </button>
        </div>
        <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
          当前章节：{chapter.title}
        </span>
      </div>

      <div className="mt-4 flex flex-col items-center gap-4">
        {readerPages.map((page, index) => (
          <figure key={page.url} className="w-full max-w-xl overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
            <img
              src={page.url}
              alt={`《${comic.title}》${chapter.title} 第${index + 1}页漫画画面`}
              title={`《${comic.title}》${chapter.title} 第${index + 1}页`}
              className="w-full object-contain"
            />
            <figcaption className="border-t border-background-200/70 bg-background-100/50 px-4 py-2 text-center text-xs text-foreground-600">
              {index + 1} / {readerPages.length} · {page.caption}
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasPrev}
          className="btn-outline !px-6 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
        >
          <i className="ri-arrow-left-line" aria-hidden="true" />
          上一章
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasNext}
          className="btn-primary !px-6 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
        >
          {chapter.isLatest ? "已是最终章" : "下一章"}
          {!chapter.isLatest && <i className="ri-arrow-right-line" aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}